import { UI, Utils } from 'common';
import kebabCase from 'lodash/kebabCase';
import PropTypes from 'prop-types';
import React from 'react';
import { CostDetails as CostDetailsEnum, FeeType } from './CostDetails.enum';
import FeesPopoverContent from './FeesPopoverContent';
import FeeUtil from './FeeUtil';

const { WrappedAccordion, WrappedAccordionTabTemplate, WrappedPopover } = UI;
const { Util } = Utils;

// Renders a breakdown of transaction fees as a list or accordion.
const FeeDetails = ({ costDetails, showUpgradeModal, renderAsAccordion }) => {
	const { filteredCostDetails, totalFees } =
		FeeUtil.filterByFeeCodesAndCalculateTotal({
			costDetails,
			feeCodes: CostDetailsEnum[FeeType.PRINT],
		});

	// Generates an array of fee item elements.
	// Each element represents a single fee with its name and amount.
	const feeItems = filteredCostDetails.map(fee => (
		<div
			key={fee.feeCode}
			className={renderAsAccordion ? 'fee-item' : 'itemized-price-group'}
		>
			<span
				className={renderAsAccordion ? 'fee-item-label' : 'service-dot-spacer'}
				data-qa={`${kebabCase(fee.feeCode)}-label`}
			>
				{fee.feeName}
			</span>
			<span
				className={
					renderAsAccordion ? 'fee-item-amount' : 'p-col-1 display-amount'
				}
				data-qa={`${kebabCase(fee.feeCode)}-amount`}
			>
				{Util.formatCurrency(fee.cost)}
			</span>
		</div>
	));

	if (renderAsAccordion) {
		return (
			<div className="fees-accordion-container">
				<WrappedAccordion>
					<WrappedAccordionTabTemplate>
						<div className="fees-accordion-header">
							<div className="fees-accordion-header-text-container">
								<div className="fees-accordion-header-text">Fees</div>
								<WrappedPopover
									triggerElement={
										<div className="fees-accordion-header-icon" />
									}
								>
									<FeesPopoverContent onUpgradeClick={showUpgradeModal} />
								</WrappedPopover>
							</div>
							<div className="fees-accordion-header-amount">
								{Util.formatCurrency(totalFees)}
							</div>
						</div>
						<div className="fee-items-container">{feeItems}</div>
					</WrappedAccordionTabTemplate>
				</WrappedAccordion>
			</div>
		);
	}

	return (
		<>
			<div className="itemized-price-group total-cost">
				<span className="service-dot-spacer">Total Fees</span>
				<span className="p-col-1 display-amount" data-qa="total-cost-amount">
					{Util.formatCurrency(totalFees)}
				</span>
			</div>
			{feeItems}
		</>
	);
};

FeeDetails.propTypes = {
	costDetails: PropTypes.arrayOf(
		PropTypes.shape({
			feeCode: PropTypes.string.isRequired,
			feeName: PropTypes.string.isRequired,
			cost: PropTypes.number.isRequired,
		})
	).isRequired,
	renderAsAccordion: PropTypes.bool,
};

FeeDetails.defaultProps = {
	renderAsAccordion: false,
};

export default FeeDetails;
